import { useState, memo, useMemo, useRef, useEffect } from "react";
import { Backdrop, Rating, Stack, Typography, Box, Menu, MenuItem } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import OffersSummary from "./OffersSummary";
import SortableTable, { TSortableColumn } from "../../common/SortableTable";
import Tag from "../../common/Tag";
import CircularProgress from "../../../../components/CircularProgress";

import {
  selectOffers,
  selectProfitCalculator,
} from "../../../../features/productAnalyzer/productAnalyzer.selector";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
  // getOffers,
  updateLookupThunk,
} from "../../../../features/productAnalyzer/productAnalyzer.thunk";
import { formatCurrency, formatPercent, toNumber } from "../../../../helpers";
// import { useProductAnalyzer } from "../../../../contexts/productAnalyzerContext";

import { useStyles } from "../RankAndPrice/index.styles";
import { TOffer } from "../../../../features/productAnalyzer/productAnalyzer.types";
// import { selectIsFreePlan } from "../../../../features/pricingPlans/pricingPlans.selector";
import useProductAnalyzerParams from "../../../../hooks/useProductAnalyzerParams";

export type TSellerOfferTableRow = TOffer & {
  sellerName: string;
  rating: number;
  ratingCount: number;
  profit: number;
  roi: number;
};

const Offers = () => {
  // const { asin } = useProductAnalyzer();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { asin, lookupId } = useProductAnalyzerParams();

  const { offers, loadingOffers } = useAppSelector(selectOffers);
  const { profitCalculator } = useAppSelector(selectProfitCalculator);
  // const isFreePlan = useAppSelector(selectIsFreePlan);

  const [isOfferChecked, setIsOfferChecked] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  // const [isLiveChecked, setIsLiveChecked] = useState(false);

  const filteredOffers = useMemo(
    () =>
      offers.filter(({ isPrime }) =>
        isOfferChecked ? Boolean(isPrime) : true,
      ) ?? [],
    [offers, isOfferChecked],
  );

  const minProfit = useMemo(() => {
    if (!filteredOffers || filteredOffers.length === 0) return 0;
    return Math.min(...filteredOffers.map((offer) => toNumber(offer.profit)));
  }, [filteredOffers]);

  const isMinProfitMet = useMemo(() => {
    if (!profitCalculator) return false;
    return minProfit >= toNumber(profitCalculator.minProfit);
  }, [minProfit, profitCalculator]);

  const TABLE_COLUMNS: TSortableColumn<TSellerOfferTableRow>[] = [
    {
      header: "Seller",
      accessorKey: "sellerName",
      align: "center",
      html: ({ sellerName, fulfillment, rating, ratingCount }) => {
        const isAmz =
          sellerName.toLowerCase() === "amazon.com" && fulfillment === "FBA";
        const tooltip = (
          <Stack>
            <Typography variant="body1">{sellerName}</Typography>
            {!isAmz && (
              <Box
                display={"flex"}
                alignItems={"center"}
                justifyContent={"flex-start"}
              >
                {!!ratingCount && (
                  <Rating
                    value={rating}
                    readOnly
                    precision={0.1}
                    sx={{ fontSize: "12px" }}
                  />
                )}
                <Typography variant="body1">
                  {ratingCount
                    ? `(${Intl.NumberFormat("en-US", {
                        useGrouping: true,
                      }).format(ratingCount)})`
                    : "No reviews yet"}
                </Typography>
              </Box>
            )}
            <Typography variant="body2">
              {fulfillment === "FBA"
                ? "Fulfilled by Amazon (Prime)"
                : "Fulfilled by Merchant"}
            </Typography>
          </Stack>
        );
        const title = isAmz
          ? "AMZ"
          : fulfillment === "FBA"
            ? "FBA"
            : fulfillment;
        const variant =
          title === "AMZ" ? "warning" : title === "FBA" ? "success" : "info";
        return (
          <Box display="flex" justifyContent="center" alignItems="center">
            <Tag tooltip={tooltip} title={title} variant={variant} badgeStyle />
          </Box>
        );
      },
    },
    {
      header: "Stock",
      accessorKey: "stock",
    },
    {
      header: "Price",
      accessorKey: "price",
      html: ({ price }) => (
        <Box
          className={`${classes.clickable}`}
          onClick={() => {
            dispatch(updateLookupThunk({ salePrice: toNumber(price) }));
          }}
        >
          {formatCurrency(price)}
        </Box>
      ),
    },
    {
      header: "Profit",
      accessorKey: "profit",
      html: ({ profit }) => {
        const isMetCriteria = profit >= toNumber(profitCalculator.minProfit);
        return (
          <Box color={isMetCriteria ? "#047647" : "#B42419"}>
            {formatCurrency(profit)}
          </Box>
        );
      },
    },
    {
      header: "ROI",
      accessorKey: "roi",
      html: ({ roi }) => {
        const value = formatPercent(roi);
        const isMetCriteria = roi >= toNumber(profitCalculator.minRoi);
        return <Box color={isMetCriteria ? "#047647" : "#B42419"}>{value}</Box>;
      },
    },
  ];

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSelectOption = (value: boolean) => {
    setIsOfferChecked(value);
    handleMenuClose();
  };

  // const handleLiveFilterChange = (value: boolean) => {
  //   setIsLiveChecked(value);
  //   dispatch(getOffers({ live: value, asin }));
  // };

  return (
    <Box>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        padding="2px 4px"
      >
        <Box
          sx={{
            borderRadius: "4px",
            backgroundColor: isMinProfitMet ? "#E9FBEF" : "#F2DEDE",
            border: `1px solid ${isMinProfitMet ? "#87E8AD" : "#A9444299"}`,
            color: isMinProfitMet ? "#15803D" : "#A94442",
            px: "8px",
            py: "2px",
            fontSize: "12px",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: "15px",
            height: "30px",
          }}
        >
          MIN PROFIT:&nbsp;<span style={{ fontWeight: 700 }}>{formatCurrency(minProfit)}</span>
        </Box>
        <Box
          onClick={handleMenuClick}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "2px 8px",
            borderRadius: "4px",
            backgroundColor: "#6B72800D",
            border: "1px solid #181D1F26",
            cursor: "pointer",
            height: "30px",
            "&:hover": {
              backgroundColor: "#E5E5E5",
            },
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              color: "#333333",
              lineHeight: "18px",
            }}
          >
            {isOfferChecked ? "Prime Only" : "All Offers"}
          </Typography>
          <Box
            sx={{
              width: "1px",
              height: "16px",
              backgroundColor: "#C9C9C9",
            }}
          />
          <KeyboardArrowDownIcon
            sx={{
              fontSize: "16px",
              color: "#6B7280",
            }}
          />
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              borderRadius: "4px",
              border: "1px solid #C9C9C9",
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
              mt: "4px",
              minWidth: "150px",
            },
          }}
        >
          <MenuItem
            onClick={() => handleSelectOption(false)}
            selected={!isOfferChecked}
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              color: "#333333",
              "&.Mui-selected": {
                backgroundColor: "#F0F0F0",
                "&:hover": {
                  backgroundColor: "#E5E5E5",
                },
              },
            }}
          >
            All Offers
          </MenuItem>
          <MenuItem
            onClick={() => handleSelectOption(true)}
            selected={isOfferChecked}
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              color: "#333333",
              "&.Mui-selected": {
                backgroundColor: "#F0F0F0",
                "&:hover": {
                  backgroundColor: "#E5E5E5",
                },
              },
            }}
          >
            Prime Only
          </MenuItem>
        </Menu>
      </Box>

      <Box position="relative">
        <SortableTable
          columns={TABLE_COLUMNS}
          data={filteredOffers}
          noFilters
          noDefaultSort
          showIndexColumn
          // lock={isFreePlan}
          asin={asin}
          lookupId={lookupId}
          title="Offers"
          // numberOfRowsToShow={isFreePlan ? 3 : 0}
        />

        <Box padding="4px 4px">
          <OffersSummary />
        </Box>

        {loadingOffers && (
          <Backdrop open={true} sx={{ position: "absolute", zIndex: 999 }}>
            <CircularProgress size="md" />
          </Backdrop>
        )}
      </Box>
    </Box>
  );
};

export default memo(Offers);
